/**
 * A screen that belongs to a module (Transport) opens only in a company
 * that has the module switched on. The server refuses the data anyway;
 * this says why, instead of a page of failed requests, and tells an
 * administrator where the switch is.
 */
import type { ReactNode } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { Button, Paper, Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { P } from '../api/types'
import { useAuth } from '../store/AuthContext'
import { modulesOf, type ModuleName } from '../nav/menu'
import { c } from '../theme'

export default function RequireModule({ name, children }:
{ name: ModuleName; children: ReactNode }) {
  const { t } = useTranslation()
  const { me, can } = useAuth()
  if (modulesOf(me).includes(name)) return <>{children}</>
  return (
    <Paper variant="outlined" sx={{ p: { xs: 3, sm: 5 }, maxWidth: 640 }}>
      <Stack spacing={1.5} alignItems="flex-start">
        <Typography component="h1" sx={{ m: 0, fontSize: 20, fontWeight: 600 }}>
          {t('modules.offTitle', { module: t(`modules.${name}.name`) })}
        </Typography>
        <Typography sx={{ color: c.inkSecondary, fontSize: 14 }}>
          {can(P.adminCompany) ? t('modules.offAdmin') : t('modules.offUser')}
        </Typography>
        <Stack direction="row" spacing={1}>
          {can(P.adminCompany) && (
            <Button variant="contained" component={RouterLink} to="/settings?tab=modules">
              {t('modules.openSettings')}</Button>
          )}
          <Button variant="outlined" component={RouterLink} to="/">{t('nav.home')}</Button>
        </Stack>
      </Stack>
    </Paper>
  )
}
